import Avatar from '@/components/linear/avatar';
import PriorityIcon from '@/components/linear/priority-icon';
import PropertyMenu from '@/components/linear/property-menu';
import StatusIcon from '@/components/linear/status-icon';
import {
  favoritePath,
  favoritesPath,
  issueAssigneePath,
  issueCommentPath,
  issueCommentsPath,
  issueLabelPath,
  issueLabelsPath,
  issuePath,
  issuePriorityPath,
  issueStatusPath,
  issueRelationPath,
  issueRelationsPath,
  issueSubIssuePath,
  issueSubIssuesPath,
  openIssuePath,
} from '@/lib/linear-paths';
import { PRIORITY_LABELS, STATUS_LABELS } from '@/lib/linear-utils';
import { formatCompactTime } from '@/lib/time';
import type { IssueComment } from '@/types/comment';
import type { Issue, IssueActivity, IssueRelation, IssueStatus } from '@/types/issue';
import type { SharedPageProps } from '@/types/shared';
import { isFavorite, toggleIssueFavorite } from '@/lib/favorites';
import { Form, Link, router, usePage } from '@inertiajs/react';
import { Plus, Star, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';

interface ServerFavorite {
  id: string;
  favoritableId?: string;
  kind?: string;
}

interface Props {
  issue: Issue;
  onClose?: () => void;
}

export default function IssueDetail({ issue, onClose }: Props) {
  const {
    teams = [],
    members = [],
    currentUser,
    labels: workspaceLabels = [],
    favorites = [],
    projects = [],
  } = usePage<
    SharedPageProps & {
      members?: Array<{ id: string; name: string; avatarColor?: string }>;
      labels?: Array<{ id: string; name: string; color: string }>;
      favorites?: ServerFavorite[];
      projects?: Array<{ id: string; name: string }>;
    }
  >().props;
  const [title, setTitle] = useState(issue.title);
  const [description, setDescription] = useState(issue.description ?? '');
  const [subTitle, setSubTitle] = useState('');
  const [estimate, setEstimate] = useState(issue.estimate ? String(issue.estimate) : '');
  const [dueOn, setDueOn] = useState(issue.dueOn ?? '');
  const [relationKind, setRelationKind] = useState<'blocks' | 'related' | 'duplicate'>('related');
  const [relationIdentifier, setRelationIdentifier] = useState('');
  const serverFavorite = favorites.find((item) => item.favoritableId === issue.id);
  const [favorited, setFavorited] = useState(() => Boolean(serverFavorite) || isFavorite(issue.id));

  const saveTitle = () => {
    if (title === issue.title) return;
    router.patch(issuePath(issue.id), { issue: { title } }, { preserveScroll: true });
  };

  const saveDescription = () => {
    if (description === (issue.description ?? '')) return;
    router.patch(issuePath(issue.id), { issue: { description } }, { preserveScroll: true });
  };

  const comments = issue.comments ?? [];
  const subIssues = issue.subIssues ?? [];
  const activities = issue.activities;
  const activityFeed = mergeActivityFeed(activities ?? [], comments);
  const showActivityEmptyState = activities !== undefined && activityFeed.length === 0;

  const toggleFavorite = () => {
    if (serverFavorite) {
      router.delete(favoritePath(serverFavorite.id), { preserveScroll: true });
      setFavorited(false);
      return;
    }
    router.post(
      favoritesPath(),
      { favorite: { favoritableType: 'Issue', favoritableId: issue.id } },
      { preserveScroll: true }
    );
    setFavorited(toggleIssueFavorite({ ...issue, title }));
  };

  const saveEstimate = () => {
    const next = estimate.trim() === '' ? null : Number(estimate);
    if (next === issue.estimate || (next === null && !issue.estimate)) return;
    router.patch(issuePath(issue.id), { issue: { estimate: next } }, { preserveScroll: true });
  };

  const saveDueOn = () => {
    const next = dueOn || null;
    if (next === (issue.dueOn ?? null)) return;
    router.patch(issuePath(issue.id), { issue: { dueOn: next } }, { preserveScroll: true });
  };

  return (
    <div className="ln-detail" style={{ height: '100%' }}>
      <div className="ln-detail-main">
        {onClose ? (
          <button
            type="button"
            className="ln-toolbar-btn"
            onClick={onClose}
            style={{ marginBottom: 16 }}
          >
            Close
          </button>
        ) : null}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <input
            className="ln-detail-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            onBlur={saveTitle}
            aria-label="Issue title"
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="ln-toolbar-btn"
            aria-label={favorited ? 'Unfavorite' : 'Favorite'}
            onClick={toggleFavorite}
          >
            <Star
              size={16}
              fill={favorited ? '#f0bf00' : 'none'}
              color={favorited ? '#f0bf00' : 'currentColor'}
            />
          </button>
        </div>
        {issue.parent ? (
          <Link
            href={openIssuePath(issue.parent)}
            className="ln-inbox-sub"
            style={{ display: 'inline-block', marginBottom: 8 }}
          >
            Parent {issue.parent.identifier} {issue.parent.title}
          </Link>
        ) : null}

        <textarea
          className="ln-detail-description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          onBlur={saveDescription}
          placeholder="Add description…"
          aria-label="Issue description"
        />

        <div className="ln-activity">
          <div className="ln-activity-title">Sub-issues</div>
          {subIssues.map((child) => (
            <div key={child.id} className="ln-activity-item" style={{ alignItems: 'center' }}>
              <StatusIcon status={child.status} />
              <Link href={openIssuePath(child)} className="ln-row-title" style={{ flex: 1 }}>
                {child.identifier} {child.title}
              </Link>
              <button
                type="button"
                className="ln-icon-btn"
                aria-label="Unlink sub-issue"
                onClick={() =>
                  router.delete(issueSubIssuePath(issue.id, child.id), { preserveScroll: true })
                }
              >
                <X size={12} />
              </button>
            </div>
          ))}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (!subTitle.trim()) return;
              router.post(
                issueSubIssuesPath(issue.id),
                { issue: { title: subTitle.trim() } },
                {
                  preserveScroll: true,
                  onSuccess: () => setSubTitle(''),
                }
              );
            }}
            style={{ display: 'flex', gap: 8, marginTop: 8 }}
          >
            <input
              className="ln-filter-input"
              style={{ flex: 1 }}
              value={subTitle}
              onChange={(event) => setSubTitle(event.target.value)}
              placeholder="Add sub-issue…"
              aria-label="Sub-issue title"
            />
            <button type="submit" className="ln-toolbar-btn" disabled={!subTitle.trim()}>
              <Plus size={14} />
              Add
            </button>
          </form>
        </div>

        <div className="ln-activity">
          <div className="ln-activity-title">Relations</div>
          <RelationList
            issueId={issue.id}
            label="Blocks"
            issues={issue.blocking ?? []}
            relations={issue.relations}
            kind="blocks"
          />
          <RelationList issueId={issue.id} label="Blocked by" issues={issue.blockedBy ?? []} />
          <RelationList
            issueId={issue.id}
            label="Related"
            issues={issue.relatedIssues ?? []}
            relations={issue.relations}
            kind="related"
          />
          <RelationList
            issueId={issue.id}
            label="Duplicate of"
            issues={issue.duplicateOf ?? []}
            relations={issue.relations}
            kind="duplicate"
          />
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (!relationIdentifier.trim()) return;
              router.post(
                issueRelationsPath(issue.id),
                {
                  relation: {
                    kind: relationKind,
                    targetIdentifier: relationIdentifier.trim().toUpperCase(),
                  },
                },
                {
                  preserveScroll: true,
                  onSuccess: () => setRelationIdentifier(''),
                }
              );
            }}
            style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}
          >
            <select
              className="ln-chip"
              value={relationKind}
              onChange={(event) =>
                setRelationKind(event.target.value as 'blocks' | 'related' | 'duplicate')
              }
              aria-label="Relation type"
            >
              <option value="related">Related</option>
              <option value="blocks">Blocks</option>
              <option value="duplicate">Duplicate of</option>
            </select>
            <input
              className="ln-filter-input"
              value={relationIdentifier}
              onChange={(event) => setRelationIdentifier(event.target.value)}
              placeholder="CAP-12"
              aria-label="Related issue identifier"
            />
            <button type="submit" className="ln-toolbar-btn" disabled={!relationIdentifier.trim()}>
              Add
            </button>
          </form>
        </div>

        <div className="ln-activity">
          <div className="ln-activity-title">Activity</div>
          {showActivityEmptyState ? (
            <div className="ln-inbox-sub">No activity yet.</div>
          ) : (
            activityFeed.map((item) => {
              if (item.kind === 'activity') {
                return (
                  <div key={`version-${item.activity.id}`} className="ln-activity-item">
                    <div>
                      <div className="ln-activity-body">
                        {activitySentence(item.activity, members, projects)}
                      </div>
                      <div className="ln-activity-meta">
                        {formatCompactTime(item.activity.createdAt)}
                      </div>
                    </div>
                  </div>
                );
              } else {
                return (
                  <div key={item.comment.id} className="ln-activity-item">
                    <Avatar
                      name={item.comment.user?.name}
                      color={item.comment.user?.avatarColor}
                      size={22}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="ln-activity-body">{item.comment.body}</div>
                      <div className="ln-activity-meta">
                        {item.comment.user?.name ?? 'Someone'} ·{' '}
                        {formatCompactTime(item.comment.createdAt)}
                      </div>
                    </div>
                    {item.comment.user?.id === currentUser?.id ? (
                      <button
                        type="button"
                        className="ln-icon-btn"
                        aria-label="Delete comment"
                        onClick={() => {
                          if (window.confirm('Delete this comment?')) {
                            router.delete(issueCommentPath(issue.id, item.comment.id), {
                              preserveScroll: true,
                            });
                          }
                        }}
                      >
                        <X size={12} />
                      </button>
                    ) : null}
                  </div>
                );
              }
            })
          )}

          <Form method="post" action={issueCommentsPath(issue.id)} className="mt-4" resetOnSuccess>
            {({ processing }) => (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <textarea
                  name="body"
                  className="ln-modal-body"
                  placeholder="Leave a comment…"
                  rows={2}
                  style={{
                    padding: 8,
                    border: '1px solid var(--ln-border)',
                    borderRadius: 8,
                    minHeight: 56,
                  }}
                  required
                />
                <button
                  type="submit"
                  className="ln-primary-btn"
                  disabled={processing}
                  style={{ marginLeft: 0 }}
                >
                  Comment
                </button>
              </div>
            )}
          </Form>
        </div>
      </div>

      <aside className="ln-properties">
        <Property label="Status">
          <PropertyMenu
            ariaLabel="Status"
            value={issue.status}
            onChange={(status) =>
              router.patch(issueStatusPath(issue.id), { status }, { preserveScroll: true })
            }
            options={(Object.entries(STATUS_LABELS) as Array<[IssueStatus, string]>).map(
              ([value, label]) => ({
                value,
                label,
                icon: <StatusIcon status={value} />,
              })
            )}
          />
        </Property>

        <Property label="Priority">
          <PropertyMenu
            ariaLabel="Priority"
            value={String(issue.priority)}
            onChange={(priority) =>
              router.patch(
                issuePriorityPath(issue.id),
                { priority: Number(priority) },
                { preserveScroll: true }
              )
            }
            options={Object.entries(PRIORITY_LABELS).map(([value, label]) => ({
              value,
              label,
              icon: <PriorityIcon priority={Number(value)} />,
            }))}
          />
        </Property>

        <Property label="Assignee">
          <PropertyMenu
            ariaLabel="Assignee"
            value={issue.assignee?.id ?? ''}
            onChange={(assigneeId) =>
              router.patch(
                issueAssigneePath(issue.id),
                { assigneeId: assigneeId || null },
                { preserveScroll: true }
              )
            }
            options={[
              { value: '', label: 'Unassigned' },
              ...members.map((member) => ({
                value: member.id,
                label: member.id === currentUser?.id ? `${member.name} (Me)` : member.name,
                icon: <Avatar name={member.name} color={member.avatarColor} size={16} />,
              })),
            ]}
          />
        </Property>

        <Property label="Team">
          <span>{issue.team?.name ?? teams[0]?.name ?? '—'}</span>
        </Property>

        <Property label="Cycle">
          <span>{issue.cycle?.name ?? 'No cycle'}</span>
        </Property>

        <Property label="Labels">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {issue.labels?.map((label) => (
              <button
                key={label.id}
                type="button"
                className="ln-label"
                style={{
                  color: label.color,
                  background: `${label.color}22`,
                  border: 'none',
                  cursor: 'pointer',
                }}
                onClick={() =>
                  router.delete(issueLabelPath(issue.id, label.id), { preserveScroll: true })
                }
              >
                {label.name}
                <X size={10} />
              </button>
            ))}
            <select
              aria-label="Add label"
              className="ln-chip"
              value=""
              onChange={(event) => {
                const labelId = event.target.value;
                if (!labelId) return;
                router.post(issueLabelsPath(issue.id), { labelId }, { preserveScroll: true });
              }}
            >
              <option value="">Add label</option>
              {workspaceLabels
                .filter((label) => !issue.labels?.some((item) => item.id === label.id))
                .map((label) => (
                  <option key={label.id} value={label.id}>
                    {label.name}
                  </option>
                ))}
            </select>
          </div>
        </Property>

        <Property label="Project">
          <span>{issue.project?.name ?? 'No project'}</span>
        </Property>

        <Property label="Estimate">
          <input
            className="ln-filter-input"
            style={{ width: 72 }}
            value={estimate}
            onChange={(event) => setEstimate(event.target.value)}
            onBlur={saveEstimate}
            inputMode="numeric"
            aria-label="Estimate"
            placeholder="—"
          />
        </Property>

        <Property label="Due date">
          <input
            className="ln-filter-input"
            type="date"
            value={dueOn}
            onChange={(event) => setDueOn(event.target.value)}
            onBlur={saveDueOn}
            aria-label="Due date"
          />
        </Property>
      </aside>
    </div>
  );
}

function RelationList({
  issueId,
  label,
  issues,
  relations,
  kind,
}: {
  issueId: string;
  label: string;
  issues: Issue[];
  relations?: IssueRelation[];
  kind?: IssueRelation['kind'];
}) {
  if (issues.length === 0) return null;

  return (
    <div style={{ marginBottom: 8 }}>
      <div className="ln-inbox-sub">{label}</div>
      {issues.map((related) => {
        const relation = relations?.find(
          (item) => item.kind === kind && item.targetId === related.id
        );
        return (
          <div key={related.id} className="ln-activity-item" style={{ alignItems: 'center' }}>
            <StatusIcon status={related.status} />
            <Link href={openIssuePath(related)} className="ln-row-title" style={{ flex: 1 }}>
              {related.identifier} {related.title}
            </Link>
            {relation ? (
              <button
                type="button"
                className="ln-icon-btn"
                aria-label="Remove relation"
                onClick={() =>
                  router.delete(issueRelationPath(issueId, relation.id), { preserveScroll: true })
                }
              >
                <X size={12} />
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function Property({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="ln-property">
      <span className="ln-property-label">{label}</span>
      {children}
    </div>
  );
}

export function statusOptions(): Array<{ value: IssueStatus; label: string }> {
  return (Object.entries(STATUS_LABELS) as Array<[IssueStatus, string]>).map(([value, label]) => ({
    value,
    label,
  }));
}

interface NamedRecord {
  id: string;
  name: string;
}

type ActivityFeedItem =
  | { kind: 'activity'; createdAt: string; activity: IssueActivity }
  | { kind: 'comment'; createdAt: string; comment: IssueComment };

function mergeActivityFeed(
  activities: IssueActivity[],
  comments: IssueComment[]
): ActivityFeedItem[] {
  const items: ActivityFeedItem[] = [
    ...activities.map((activity) => ({
      kind: 'activity' as const,
      createdAt: activity.createdAt,
      activity,
    })),
    ...comments.map((comment) => ({
      kind: 'comment' as const,
      createdAt: comment.createdAt,
      comment,
    })),
  ];

  return items.sort((left, right) => right.createdAt.localeCompare(left.createdAt));
}

function activitySentence(
  activity: IssueActivity,
  members: NamedRecord[],
  projects: NamedRecord[]
): string {
  const actor =
    activity.userName && activity.userName.trim() !== '' ? activity.userName : 'Someone';

  if (activity.event === 'create') {
    return `${actor} created the issue`;
  } else if (activity.event === 'destroy') {
    return `${actor} deleted the issue`;
  } else {
    const parts = activityChangeParts(activity.changeset, members, projects);
    if (parts.length === 0) {
      return `${actor} updated the issue`;
    } else {
      return `${actor} ${parts.join(', ')}`;
    }
  }
}

function activityChangeParts(
  changeset: IssueActivity['changeset'],
  members: NamedRecord[],
  projects: NamedRecord[]
): string[] {
  const parts: string[] = [];
  if (!changeset) {
    return parts;
  }

  const status = changesetPair(changeset, 'status');
  if (status) {
    const next = String(status[1] ?? '');
    const label = STATUS_LABELS[next as IssueStatus] ?? next;
    parts.push(`changed status to ${label}`);
  }

  const title = changesetPair(changeset, 'title');
  if (title) {
    parts.push(`changed title to ${String(title[1] ?? '')}`);
  }

  const description = changesetPair(changeset, 'description');
  if (description) {
    parts.push('updated the description');
  }

  const priority = changesetPair(changeset, 'priority');
  if (priority) {
    const next = Number(priority[1]);
    if (PRIORITY_LABELS[next]) {
      parts.push(`changed priority to ${PRIORITY_LABELS[next]}`);
    } else {
      parts.push(`changed priority to ${String(priority[1] ?? '')}`);
    }
  }

  const assignee = changesetPair(changeset, 'assigneeId', 'assignee_id');
  if (assignee) {
    const nextId = assignee[1];
    if (nextId) {
      const member = members.find((item) => item.id === String(nextId));
      if (member) {
        parts.push(`assigned to ${member.name}`);
      } else {
        parts.push('assigned to someone');
      }
    } else {
      parts.push('removed the assignee');
    }
  }

  const project = changesetPair(changeset, 'projectId', 'project_id');
  if (project) {
    const nextId = project[1];
    if (nextId) {
      const named = projects.find((item) => item.id === String(nextId));
      if (named) {
        parts.push(`moved to ${named.name}`);
      } else {
        parts.push('moved to a project');
      }
    } else {
      parts.push('removed the project');
    }
  }

  const dueOn = changesetPair(changeset, 'dueOn', 'due_on');
  if (dueOn) {
    const next = dueOn[1];
    if (next) {
      parts.push(`set due date to ${String(next)}`);
    } else {
      parts.push('cleared the due date');
    }
  }

  const estimate = changesetPair(changeset, 'estimate');
  if (estimate) {
    const next = estimate[1];
    if (next === null || next === undefined || next === '') {
      parts.push('cleared the estimate');
    } else {
      parts.push(`set estimate to ${String(next)}`);
    }
  }

  return parts;
}

function changesetPair(
  changeset: Record<string, unknown[] | undefined>,
  ...keys: string[]
): unknown[] | undefined {
  let match: unknown[] | undefined;
  for (const key of keys) {
    const value = changeset[key];
    if (Array.isArray(value) && value.length >= 2) {
      match = value;
      break;
    }
  }
  return match;
}
